import { Box, Grid, TextField } from "@mui/material"
import { Controller, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next";
import { InsuranceProfileService } from "subModule/src/core/services";
import AlertMessages from "common/Alert/AlertMessages";
import { useSnackbar } from "notistack";
import SimpleModal from "common/Form/Form";
import { useNavigate, useParams } from "react-router-dom";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import PolicyIcon from '@mui/icons-material/Policy';
import AssuredWorkloadOutlinedIcon from '@mui/icons-material/AssuredWorkloadOutlined';
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import Loader from "layout/Loader";
import VerticalTabs from "common/ComponentSidebar/ComponentSideBar";
import BasicDatePicker from "common/Pickers/Pickers";
import { GetInsurancePolicyByIdResponse } from "@subModule/src/services/insurance/interface";
import ComponentSubheader from "common/ComponentHeaderTwo/ComponentSubheader";
import type { BreadcrumbItem } from "Helpers/generalBreadcrumbs";
interface FormData { [key: string]: any; }
export default function InsurancePoliciesModal() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const params = useParams();
    const companyId = params?.id;
    const isPolicyEdit = !!params?.policyId;
    const { enqueueSnackbar } = useSnackbar();
    const { register, control, handleSubmit, 
        formState: { errors, dirtyFields, isDirty }, 
        reset, setValue, trigger, getValues
    } = useForm<FormData>({
        defaultValues: {}
    });
    const [loading, setLoading] = useState<boolean>(params?.policyId ? true : false);
    const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(params?.policyId ? false : true);
    const [companyTitle, setCompanyTitle] = useState('');
    const [policyTitle, setPolicyTitle] = useState('');

    useEffect(() => {
        if (!companyId) return;
        let active = true;
        InsuranceProfileService.getInsuranceCompanyByID(+companyId)
            .then((res: any) => {
                if (!active) return;
                const name =
                    res?.data?.title ||
                    res?.data?.secondaryTitle ||
                    res?.title ||
                    "";
                if (name) setCompanyTitle(String(name).trim());
            })
            .catch(() => {});
        return () => {
            active = false;
        };
    }, [companyId]);

    useEffect(() => {
        if (params?.policyId) {
            if (loading) getByID();
        }
    }, [params]);
    useEffect(() => {
        if (isDirty && params?.policyId) setIsBtnEnabled(true);
    }, [isDirty, dirtyFields]);
    const getByID = () => {
        InsuranceProfileService.getInsurancePolicyByID(+(params as any)?.policyId).then((res: GetInsurancePolicyByIdResponse) => {
            if (res.success) {
                reset({
                    ...res?.data || {},
                });
                setPolicyTitle(String(res?.data?.title || "").trim());
                setLoading(false);
            } else {
                enqueueSnackbar(<AlertMessages text={res.message || "Something went wrong"} />, { variant: 'error' });
            }
        }).catch((err: any) => { console.error("===", err); });
    }
    const onSubmit = (data: any) => {
        setIsBtnEnabled(false);
        if (params?.policyId) {
            InsuranceProfileService.updateInsurancePolicy(Number(params?.policyId), {
                ...data,
                agreedDiscountPart: +data?.agreedDiscountPart || 0,
                agreedDiscountService: +data?.agreedDiscountService || 0,
                agreedDiscountPackage: +data?.agreedDiscountPackage || 0,
                accountId: +(params as any)?.id,
            }).then((response: any) => {
                if (response.success) {
                    setPolicyTitle(String(data?.title || "").trim());
                    enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('INSURANCEPOLICY') + ' ' + t('updated') + ' ' + t('successfully')} />, { variant: 'success' });
                    navigate(-1);
                } else {
                    setIsBtnEnabled(true);
                    enqueueSnackbar(<AlertMessages text={t(response?.message)} />, { variant: 'error' });
                }
            }).catch((err: any) => {
                console.error("===", err);
                setIsBtnEnabled(true);
                enqueueSnackbar(<AlertMessages text={err?.message || err?.data?.message || "Something went wrong"} />, { variant: 'error' });
            });
        } else {
            InsuranceProfileService.createInsurancePolicy({
                ...data,
                agreedDiscountPart: +data?.agreedDiscountPart || 0,
                agreedDiscountService: +data?.agreedDiscountService || 0,
                agreedDiscountPackage: +data?.agreedDiscountPackage || 0,
                accountId: +(params as any)?.id,
            }).then((response: any) => {
                if (response.success) {
                    enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('INSURANCEPOLICY') + ' ' + t('created') + ' ' + t('successfully')} />, { variant: 'success' });
                    navigate(-1);
                } else {
                    setIsBtnEnabled(true);
                    enqueueSnackbar(<AlertMessages text={t(response?.message)} />, { variant: 'error' });
                }
            }).catch((err: any) => {
                console.error("===", err);
                setIsBtnEnabled(true);
                enqueueSnackbar(<AlertMessages text={err?.message || err?.data?.message || "Something went wrong"} />, { variant: 'error' });
            });
        }
    };
    const handleChangeTabs = (tabId: number) => {
        if (tabId === 0) navigate(`/insurance-company/${params?.id}`);
        else if (tabId === 1) navigate(`/insurance-company/${params?.id}/insurance-policies`);
    }
    const tabs = [
        { id: 0, title: `edit`, child: <></> },
        { id: 1, title: `insurancePolicies`, child: <></> },
    ];
    const disableTabs = tabs.map(tab => tab.id).filter(tabId => {
        switch (tabId) {
            case 0:
                return !checkPrivileges('ROLE_INSURANCECOMPANY_UPDATE');
            case 1:
                return !checkPrivileges('ROLE_INSURANCEPOLICY_VIEW');
            default:
                return false;
        }
    });

    const breadcrumbItems: BreadcrumbItem[] = useMemo(() => {
        const base: BreadcrumbItem[] = [
            { labelKey: "businessSetup" },
            { labelKey: "insuranceCompanies", href: "/insurance-companies" },
            {
                labelKey: "insuranceCompany",
                ...(companyTitle ? { label: companyTitle } : {}),
                href: companyId ? `/insurance-company/${companyId}` : undefined,
            },
            {
                labelKey: "insurancePolicies",
                href: companyId
                    ? `/insurance-company/${companyId}/insurance-policies`
                    : undefined,
            },
        ];
        if (!isPolicyEdit) {
            return [...base, { labelKey: "create" }];
        }
        return [
            ...base,
            {
                labelKey: "INSURANCEPOLICY",
                ...(policyTitle ? { label: policyTitle } : {}),
            },
            { labelKey: "edit" },
        ];
    }, [companyId, companyTitle, isPolicyEdit, policyTitle]);

    return (<>
        <ComponentHeaderTwo
            showBtn={true}
            showSaveBtn={false}
            onClick={() => navigate('/insurance-companies')}
            heading={t('insuranceCompanies')}
            icon={<AssuredWorkloadOutlinedIcon sx={{ fill: '#000', width: '30px', height: '30px' }} />}
            btnText={"back"}
            btnType={"back"}
            showCreateBtn={false}
            breadcrumbItems={breadcrumbItems}
        />
        <Box className='flex'>
            <VerticalTabs tabs={tabs} selectedIndex={1} handleChangeTabs={handleChangeTabs} disableTabNo={disableTabs} />
            {loading ? <Loader /> : <div className='w-[100%] mt-[10px]'>
                <ComponentSubheader
                    showBtn={false}
                    showSaveBtn={false}
                    onClick={() => navigate(-1)}
                    onSubmit={handleSubmit(onSubmit)}
                    heading={params?.policyId ? t('editInsurancePolicy') : t('addInsurancePolicy')}
                    icon={<PolicyIcon sx={{ fill: '#000', width: '30px', height: '30px' }} />}
                    btnText={"back"}
                    btnType={"back"}
                    isBtnEnabled={IsBtnEnabled}
                    showCreateBtn={checkPrivileges("ROLE_INSURANCEPOLICY_CREATE")}
                />
                <SimpleModal
                    visable={true}
                    title={params?.policyId ? "editInsurancePolicy" : "addInsurancePolicy"}
                    isButtonVisable={true}
                    isVisableBtn={IsBtnEnabled}
                    btnclosetext="Back"
                    buttonText={params?.policyId ? "update" : "submit"}
                    attBtnNotshow={false}
                    notVisableBackbtn={true}
                    showPortList={handleSubmit(onSubmit)}
                    formSubmit={handleSubmit(onSubmit)}
                    content={
                        <Grid container component="form" id="form" rowSpacing={3} columnSpacing={2} onSubmit={handleSubmit(onSubmit)}>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    {...register("title", { validate: { required: (value) => value.trim() !== '' || t('This field is required.'), } })}
                                    label={t('title') + ' *'}
                                    placeholder={t('title')}
                                    title={t('title')}
                                    fullWidth
                                    error={!!errors.title}
                                    helperText={errors.title?.message as string}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    {...register("policyNumber", { validate: { required: (value) => value.trim() !== '' || t('This field is required.'), } })}
                                    label={t('policyNumber') + ' *'}
                                    InputLabelProps={{ shrink: true }}
                                    placeholder={t('policyNumber')}
                                    title={t('policyNumber')}
                                    fullWidth
                                    error={!!errors.policyNumber}
                                    helperText={errors.policyNumber?.message as string}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    type="number"
                                    {...register("paymentTerms", { 
                                        required: t('This field is required.'),
                                        validate: value => value > 0 || t('amountGreaterThanZero')
                                    })}
                                    label={t('payment') + ' ' + t('Terms') + ' (' + t("days") + ') *'}
                                    placeholder={t('payment') + ' ' + t('Terms') + ' (' + t("days") + ') *'}
                                    title={t('payment') + ' ' + t('Terms') + ' (' + t("days") + ') *'}
                                    fullWidth
                                    error={!!errors.paymentTerms}
                                    helperText={errors.paymentTerms?.message as string}
                                    InputLabelProps={{ shrink: true }}
                                    onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                                        const cleaned = e.target.value.replace(/[^0-9]/g, "");
                                        setValue("paymentTerms", +cleaned);
                                        trigger('paymentTerms');
                                        if(!IsBtnEnabled) setIsBtnEnabled(true);
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    type="number"
                                    {...register("agreedDiscountPart", { 
                                        required: false,
                                        validate: value => !value || value >= 0 || t('amountGreaterThanZero')
                                    })}
                                    label={t("discountOnParts") + ' (%)'}
                                    placeholder={t("discountOnParts") + ' (%)'}
                                    title={t("discountOnParts") + ' (%)'}
                                    fullWidth
                                    error={!!errors.agreedDiscountPart}
                                    helperText={errors.agreedDiscountPart?.message as string}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    type="number"
                                    {...register("agreedDiscountService", { 
                                        required: false,
                                        validate: value => !value || value >= 0 || t('amountGreaterThanZero')
                                    })}
                                    label={t("discountOnServices") + ' (%)'}
                                    placeholder={t("discountOnServices") + ' (%)'}
                                    title={t("discountOnServices") + ' (%)'}
                                    fullWidth
                                    error={!!errors.agreedDiscountService}
                                    helperText={errors.agreedDiscountService?.message as string}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    type="number"
                                    {...register("agreedDiscountPackage", { 
                                        required: false,
                                        validate: value => !value || value >= 0 || t('amountGreaterThanZero')
                                    })}
                                    label={t("discountOnPackages") + ' (%)'}
                                    placeholder={t("discountOnPackages") + ' (%)'}
                                    title={t("discountOnPackages") + ' (%)'}
                                    fullWidth
                                    error={!!errors.agreedDiscountPackage}
                                    helperText={errors.agreedDiscountPackage?.message as string}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Controller
                                    name="startDate"
                                    control={control}
                                    rules={{ validate: (value) => {
                                        const date = new Date(value);
                                        return isNaN(date.getTime()) ? t("Invalid Date") : true;
                                    } }}
                                    render={({ field }) => (
                                        <BasicDatePicker
                                            type="datePicker"
                                            {...field}
                                            label={t('startDate') + ' *'}
                                            fullWidth
                                            onChange={(value: any) => {field.onChange(new Date(value).toLocaleDateString('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' })?.split('/').reverse().join('-')); trigger('startDate');}}
                                            required={false}
                                            error={errors.startDate?.message as string}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Controller
                                    name="expiryDate"
                                    control={control}
                                    rules={{ validate: (value) => {
                                        const date = new Date(value);
                                        if (isNaN(date.getTime())) return t("Invalid Date");
                                        const startDate = new Date(getValues("startDate"));
                                        if (startDate && date <= startDate) return t("mustBeAfterStartDate");
                                        return true;
                                    } }}
                                    render={({ field }) => (
                                        <BasicDatePicker
                                            type="datePicker"
                                            {...field}
                                            label={t('expiryDate') + ' *'}
                                            fullWidth
                                            onChange={(value: any) => {field.onChange(new Date(value).toLocaleDateString('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' })?.split('/').reverse().join('-')); trigger('expiryDate');}}
                                            required={false}
                                            error={errors.expiryDate?.message as string}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    {...register("description")}
                                    label={t('description')}
                                    placeholder={t('description')}
                                    title={t('description')}
                                    fullWidth
                                    error={!!errors.description}
                                    helperText={errors.description?.message as string}
                                    InputLabelProps={{ shrink: true }}
                                    multiline
                                    rows={1}
                                    sx={{
                                        '& .MuiInputBase-fullWidth.MuiInputBase-multiline': { width: '100%', paddingRight: '0px', paddingBottom: 0 },
                                        '& .MuiInputBase-inputMultiline': { resize: 'vertical', minHeight: '29px' }
                                    }}
                                />
                            </Grid>
                        </Grid>
                    }
                />
            </div>}
        </Box>
    </>)
}